import { z } from 'zod';
import { QUALIFICATION_FIELD_LABELS, QUALIFICATION_FIELDS } from './prompts.js';

// Setter funnel (spec §05.1, §15.5, §08.9) and per-project qualification (spec §04, §08.3, §15.6).
// The commercial MEANING of every stage is fixed here; CRM pipelines only map onto it (§15.5).

const Iso = z.string();
const Id = z.string().min(1).max(64);

// ───────────── Stages ─────────────

export const FUNNEL_STAGES = [
  'assigned',
  'contacted',
  'conversation_open',
  'qualified',
  'appointment_scheduled',
  'appointment_completed',
  'no_show',
  'appointment_cancelled',
  'appointment_rescheduled',
  'future_potential',
  'lost',
] as const;
export const FunnelStageSchema = z.enum(FUNNEL_STAGES);
export type FunnelStage = z.infer<typeof FunnelStageSchema>;

export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  assigned: 'Asignado',
  contacted: 'Contactado',
  conversation_open: 'Conversación abierta',
  qualified: 'Calificado',
  appointment_scheduled: 'Cita agendada',
  appointment_completed: 'Cita realizada',
  no_show: 'No show',
  appointment_cancelled: 'Cancelada',
  appointment_rescheduled: 'Reprogramada',
  future_potential: 'Potencial futuro',
  lost: 'Perdido',
};

/** Fixed commercial meaning (§05.1 / §15.5) — shown next to the stage and in the CRM mapping editor. */
export const FUNNEL_STAGE_MEANINGS: Record<FunnelStage, string> = {
  assigned: 'Oportunidad creada y en cola, sin intento de contacto efectuado.',
  contacted: 'Se inició una llamada o se envió un WhatsApp permitido, aunque no haya respuesta.',
  conversation_open: 'Hubo un intercambio bidireccional real con el prospecto.',
  qualified: 'Criterios obligatorios del proyecto confirmados en conversación, aunque no haya cita.',
  appointment_scheduled: 'El prospecto aceptó fecha y hora y la reserva real quedó confirmada.',
  appointment_completed: 'Resultado informado por el equipo: la reunión se realizó.',
  no_show: 'Resultado informado por el equipo: el prospecto no asistió.',
  appointment_cancelled: 'La cita se canceló (informado por el equipo o por el calendario).',
  appointment_rescheduled: 'La cita se reprogramó con una nueva reserva confirmada.',
  future_potential: 'Interés genuino con una fecha o condición concreta para retomar.',
  lost: 'Cerrada con un motivo estructurado del catálogo y evidencia.',
};

/** Stages that end LIA's active pursuit of the opportunity (no automatic work continues). */
export const CLOSED_FUNNEL_STAGES: readonly FunnelStage[] = ['lost', 'appointment_completed'];
/** Stages that are not actively worked right now (closed + parked). */
export const INACTIVE_FUNNEL_STAGES: readonly FunnelStage[] = ['lost', 'appointment_completed', 'future_potential'];

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

/** Legacy keys stored before the fixed catalogue, plus common CRM/CSV spellings. */
const STAGE_ALIASES: Record<string, FunnelStage> = {
  new: 'assigned',
  nuevo: 'assigned',
  asignado: 'assigned',
  primer_contacto: 'contacted',
  contactado: 'contacted',
  conversacion_abierta: 'conversation_open',
  calificado: 'qualified',
  appointment: 'appointment_scheduled',
  cita: 'appointment_scheduled',
  cita_agendada: 'appointment_scheduled',
  con_cita: 'appointment_scheduled',
  cita_realizada: 'appointment_completed',
  reunion_realizada: 'appointment_completed',
  realizada: 'appointment_completed',
  noshow: 'no_show',
  cancelada: 'appointment_cancelled',
  cita_cancelada: 'appointment_cancelled',
  appointment_canceled: 'appointment_cancelled',
  reprogramada: 'appointment_rescheduled',
  rescheduled: 'appointment_rescheduled',
  potencial_futuro: 'future_potential',
  disqualified: 'lost',
  descartado: 'lost',
  perdido: 'lost',
};

/** Parses a stage key, a legacy key or a Spanish label (accent/case-insensitive). Unknown → null (never guessed). */
export function parseFunnelStage(raw: string | null | undefined): FunnelStage | null {
  if (!raw) return null;
  const k = fold(raw);
  if ((FUNNEL_STAGES as readonly string[]).includes(k)) return k as FunnelStage;
  return STAGE_ALIASES[k] ?? null;
}

/** Accepts legacy spellings on input (e.g. stored routine definitions with "new"); output is always a fixed key. */
export const FunnelStageInputSchema = z.preprocess((v) => (typeof v === 'string' ? (parseFunnelStage(v) ?? v) : v), FunnelStageSchema);

export const STAGE_CHANGE_SOURCES = ['event', 'evaluator', 'calendar', 'human', 'crm', 'ingress', 'routine', 'ai', 'migration'] as const;
export const StageChangeSourceSchema = z.enum(STAGE_CHANGE_SOURCES);
export type StageChangeSource = z.infer<typeof StageChangeSourceSchema>;
/** Changes made (or synced) by the commercial team prevail over automation (§08.7). */
export const HUMAN_STAGE_SOURCES: readonly StageChangeSource[] = ['human', 'crm', 'ingress'];
export const STAGE_CHANGE_SOURCE_LABELS: Record<StageChangeSource, string> = {
  event: 'Evento real',
  evaluator: 'Evaluación de calificación',
  calendar: 'Calendario',
  human: 'Equipo comercial',
  crm: 'CRM',
  ingress: 'Sistema externo',
  routine: 'Rutina',
  ai: 'Propuesta de la IA (validada)',
  migration: 'Migración',
};

// ───────────── Loss reasons (§08.9) ─────────────

export const BASE_LOSS_REASONS = [
  'not_contacted',
  'not_interested',
  'budget_mismatch',
  'product_or_zone_mismatch',
  'no_show_unrecovered',
  'cancelled_not_rescheduled',
  'misuse',
] as const;
export type BaseLossReason = (typeof BASE_LOSS_REASONS)[number];
/** Reporting categories: the 7 common reasons plus "other" for developer extensions that fit none of them. */
export const LOSS_REASON_CATEGORIES = [...BASE_LOSS_REASONS, 'other'] as const;
export const LossReasonCategorySchema = z.enum(LOSS_REASON_CATEGORIES);
export type LossReasonCategory = z.infer<typeof LossReasonCategorySchema>;
export const LOSS_REASON_LABELS: Record<LossReasonCategory, string> = {
  not_contacted: 'No contactado',
  not_interested: 'No interesado',
  budget_mismatch: 'Presupuesto incompatible',
  product_or_zone_mismatch: 'Producto o zona incompatible',
  no_show_unrecovered: 'No Show — No recuperado',
  cancelled_not_rescheduled: 'Cita cancelada sin reagendar',
  misuse: 'Uso indebido / Sin intención comercial',
  other: 'Otro (extensión del desarrollador)',
};
export const LOSS_REASON_DESCRIPTIONS: Record<BaseLossReason, string> = {
  not_contacted: 'Terminó la cadencia aprobada sin conversación.',
  not_interested: 'El prospecto manifestó explícitamente que no desea continuar.',
  budget_mismatch: 'Presupuesto confirmado incompatible, sin alternativa autorizada ni potencial futuro concreto.',
  product_or_zone_mismatch: 'Los proyectos autorizados del desarrollador no cubren las necesidades confirmadas.',
  no_show_unrecovered: 'Terminó la cadencia de recuperación de no show sin respuesta o reagenda.',
  cancelled_not_rescheduled: 'Terminó el seguimiento aprobado después de la cancelación.',
  misuse: 'Evidencia de abuso deliberado (no aplica solo por silencio o conversación larga).',
};
export const isBaseLossReason = (k: string): k is BaseLossReason => (BASE_LOSS_REASONS as readonly string[]).includes(k);

export const LossReasonSchema = z.object({
  /** A base code, or the id of a developer/project extension. */
  key: z.string(),
  label: z.string(),
  category: LossReasonCategorySchema,
  base: z.boolean(),
  description: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  active: z.boolean(),
});
export type LossReason = z.infer<typeof LossReasonSchema>;
export const LossReasonListQuerySchema = z.object({ projectId: Id.optional(), includeInactive: z.enum(['true', 'false']).optional() });
export const CreateLossReasonSchema = z.object({
  label: z.string().trim().min(2).max(120),
  category: LossReasonCategorySchema.default('other'),
  description: z.string().trim().max(500).nullable().optional(),
  /** null = every project of the developer (workspace). */
  projectId: Id.nullable().optional(),
});
export const UpdateLossReasonSchema = z.object({
  label: z.string().trim().min(2).max(120).optional(),
  category: LossReasonCategorySchema.optional(),
  description: z.string().trim().max(500).nullable().optional(),
  active: z.boolean().optional(),
});

// ───────────── Stage changes ─────────────

export const StageTransitionSchema = z.object({
  id: Id,
  fromStage: FunnelStageSchema.nullable(),
  toStage: FunnelStageSchema,
  source: StageChangeSourceSchema,
  actorName: z.string().nullable(),
  /** What triggered it (e.g. "WhatsApp enviado", "Reserva confirmada en Calendly"). */
  reason: z.string(),
  evidence: z.record(z.string(), z.unknown()),
  lossReason: LossReasonSchema.pick({ key: true, label: true, category: true }).nullable(),
  note: z.string().nullable(),
  followUpAt: Iso.nullable(),
  followUpCondition: z.string().nullable(),
  createdAt: Iso,
});
export type StageTransition = z.infer<typeof StageTransitionSchema>;

export const SetLeadStageSchema = z
  .object({
    stage: FunnelStageInputSchema,
    /** Required for Perdido: a base code or an extension id valid for the lead's project. */
    lossReason: z.string().trim().min(1).max(64).nullable().optional(),
    /** Brief explanation / evidence (required for Perdido and Potencial futuro). */
    note: z.string().trim().max(1_000).nullable().optional(),
    followUpAt: Iso.nullable().optional(),
    followUpCondition: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.stage === 'lost' && !v.lossReason) ctx.addIssue({ code: 'custom', path: ['lossReason'], message: 'Perdido requiere un motivo del catálogo' });
    if ((v.stage === 'lost' || v.stage === 'future_potential') && !v.note?.trim()) ctx.addIssue({ code: 'custom', path: ['note'], message: 'Explica brevemente la evidencia' });
    if (v.stage === 'future_potential' && !v.followUpAt && !v.followUpCondition?.trim())
      ctx.addIssue({ code: 'custom', path: ['followUpAt'], message: 'Potencial futuro requiere una fecha o una condición para retomar' });
    if (v.followUpAt && Number.isNaN(Date.parse(v.followUpAt))) ctx.addIssue({ code: 'custom', path: ['followUpAt'], message: 'Fecha no válida' });
  });
export type SetLeadStage = z.infer<typeof SetLeadStageSchema>;

// ───────────── Qualification (§04, §08.3, §15.6) ─────────────

export const CRITERION_TIERS = ['mandatory', 'desirable', 'informative'] as const;
export const CriterionTierSchema = z.enum(CRITERION_TIERS);
export type CriterionTier = z.infer<typeof CriterionTierSchema>;
export const CRITERION_TIER_LABELS: Record<CriterionTier, string> = { mandatory: 'Obligatorio', desirable: 'Deseable', informative: 'Informativo' };

export const QUALIFICATION_VALUE_STATUSES = ['confirmed', 'declared', 'pending', 'inferred'] as const;
export const QualificationValueStatusSchema = z.enum(QUALIFICATION_VALUE_STATUSES);
export type QualificationValueStatus = z.infer<typeof QualificationValueStatusSchema>;
export const QUALIFICATION_VALUE_STATUS_LABELS: Record<QualificationValueStatus, string> = {
  confirmed: 'Confirmado',
  declared: 'Declarado (sin confirmar)',
  pending: 'Pendiente',
  inferred: 'Inferido',
};

export const QUALIFICATION_VALUE_SOURCES = ['ai', 'advisor', 'crm', 'form', 'import', 'copilot'] as const;
export const QualificationValueSourceSchema = z.enum(QUALIFICATION_VALUE_SOURCES);
export type QualificationValueSource = z.infer<typeof QualificationValueSourceSchema>;

/** Core criteria the spec requires every project to consider (§15.6): budget, zone, typology/needs, purpose. */
export const CORE_CRITERIA = ['budget', 'preferred_area', 'preferred_unit_type', 'purpose'] as const;

export const CriterionKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Usa minúsculas, números y guion bajo (2–40 caracteres)');

export const QualificationCriterionSchema = z.object({
  key: CriterionKeySchema,
  label: z.string().trim().min(2).max(80),
  tier: CriterionTierSchema,
  /** Built-in fields map onto the shared qualification fields; custom ones are project-specific. */
  custom: z.boolean(),
  description: z.string().trim().max(300).nullable().default(null),
});
export type QualificationCriterion = z.infer<typeof QualificationCriterionSchema>;

const QualificationConfigBase = z.object({
  criteria: z.array(QualificationCriterionSchema).max(30),
  /** §04.d: book a visit with mandatory criteria still pending (never shown as Calificado). */
  allowBookingWithPending: z.boolean(),
});
export const QualificationConfigSchema = QualificationConfigBase.superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.criteria.forEach((c, i) => {
      if (seen.has(c.key)) ctx.addIssue({ code: 'custom', path: ['criteria', i, 'key'], message: 'Clave repetida' });
      seen.add(c.key);
      const builtin = (QUALIFICATION_FIELDS as readonly string[]).includes(c.key);
      if (c.custom && builtin) ctx.addIssue({ code: 'custom', path: ['criteria', i, 'key'], message: 'Esa clave pertenece a un criterio base' });
      if (!c.custom && !builtin) ctx.addIssue({ code: 'custom', path: ['criteria', i, 'key'], message: 'Criterio base desconocido' });
    });
  });
export type QualificationConfig = z.infer<typeof QualificationConfigSchema>;

/** Defaults while the business has not configured the project (PEND-03): the four core criteria are mandatory. */
export function defaultQualificationConfig(): QualificationConfig {
  const tier = (k: string): CriterionTier => ((CORE_CRITERIA as readonly string[]).includes(k) ? 'mandatory' : 'informative');
  return {
    criteria: QUALIFICATION_FIELDS.map((k) => ({ key: k, label: QUALIFICATION_FIELD_LABELS[k], tier: tier(k), custom: false, description: null })),
    allowBookingWithPending: false,
  };
}

export const QualificationConfigResponseSchema = QualificationConfigBase.extend({ configured: z.boolean(), updatedAt: Iso.nullable() });
export type QualificationConfigResponse = z.infer<typeof QualificationConfigResponseSchema>;

export const QualificationHistoryEntrySchema = z.object({
  id: Id,
  value: z.string(),
  status: QualificationValueStatusSchema,
  source: z.string(),
  channel: z.string().nullable(),
  evidenceRef: z.string().nullable(),
  actorName: z.string().nullable(),
  /** false when a newer, stronger value was already current (kept as history, did not replace it). */
  applied: z.boolean(),
  recordedAt: Iso,
});
export type QualificationHistoryEntry = z.infer<typeof QualificationHistoryEntrySchema>;

export const CriterionStateSchema = z.object({
  key: z.string(),
  label: z.string(),
  tier: CriterionTierSchema,
  custom: z.boolean(),
  value: z.string().nullable(),
  /** pending when there is no value yet. */
  status: QualificationValueStatusSchema,
  source: z.string().nullable(),
  at: Iso.nullable(),
  history: z.array(QualificationHistoryEntrySchema),
});
export type CriterionState = z.infer<typeof CriterionStateSchema>;

export const QualificationEvaluationSchema = z.object({
  qualified: z.boolean(),
  /** Mandatory criteria not yet confirmed in conversation (visible to the advisor). */
  pendingMandatory: z.array(z.object({ key: z.string(), label: z.string(), status: QualificationValueStatusSchema })),
  allowBookingWithPending: z.boolean(),
  criteria: z.array(CriterionStateSchema),
});
export type QualificationEvaluation = z.infer<typeof QualificationEvaluationSchema>;

export const SetQualificationValueSchema = z.object({
  value: z.string().trim().min(1).max(300),
  /** An advisor records what the prospect confirmed (default) or what still needs confirmation. */
  status: z.enum(['confirmed', 'declared', 'pending']).default('confirmed'),
  note: z.string().trim().max(500).nullable().optional(),
});

// ───────────── Lead funnel read model ─────────────

export const LeadFunnelSchema = z.object({
  interestId: Id,
  projectId: Id,
  projectName: z.string(),
  stage: FunnelStageSchema,
  stageSource: StageChangeSourceSchema,
  stageChangedAt: Iso.nullable(),
  lossReason: LossReasonSchema.pick({ key: true, label: true, category: true }).nullable(),
  lossNote: z.string().nullable(),
  followUpAt: Iso.nullable(),
  followUpCondition: z.string().nullable(),
  history: z.array(StageTransitionSchema),
  /**
   * §15.7 invariant: an opportunity under LIA always has a next action or a coherent final state. `coherent: false`
   * means nothing is scheduled (no routine step, task, appointment or follow-up) and the stage is not final.
   */
  nextAction: z.object({ coherent: z.boolean(), detail: z.string() }),
  qualification: QualificationEvaluationSchema,
  /** Loss reasons a human may pick for this lead's project. */
  lossReasons: z.array(LossReasonSchema),
});
export type LeadFunnel = z.infer<typeof LeadFunnelSchema>;
